import React, { Component } from "react";
import * as Yup from "yup";
import axios from "axios";
import { Formik } from "formik";
import { withRouter } from "react-router-dom";
import { normalize } from "normalizr";
import * as Sentry from "@sentry/browser";

import i18n from "../../../../constants/i18n";
import { photo as photoSchema } from "../../../../schemas";
import { block as blockSchema } from "../../../../schemas";
import createApiService from "../../../../network";
import ValidationError from "../../../../helpers/ValidationError";
import handleUploadProgress from "../../../../helpers/handleUploadProgress";

import BlockPhotoUploadModalView from "./BlockPhotoUploadModalView";
import { extractApiErrorMessage } from "../../../../helpers";
import { validateImageResolution } from '../../../../helpers/validateImageResolution';
import { validateFileSizeLimit } from '../../../../helpers/validateFileSizeLimit';

const supportedFormats = [
    "image/jpg",
    "image/jpeg",
    "image/png",
    "image/heif",
    "image/heic"
];

class BlockPhotoUploadModalContainer extends Component {
    initialValues = {
        photo: ""
    };

    constructor(props) {
        super(props);

        this.api = createApiService(axios);
    }

    baseValidationSchema = Yup.object().shape({
        photo: Yup.mixed()
            .required(i18n.validation.required)
            .test("fileType", i18n.validation.fileType, value => {
                if (!value) return false;
                if (value instanceof FileList) {
                    return Array.from(value).every(file =>
                        supportedFormats.includes(file.type)
                    );
                }
                return supportedFormats.includes(value.type);
            })
            .test("fileLimit", i18n.validation.fileLimit, value => {
                if (!value) return false;
                if (value instanceof FileList) {
                    return Array.from(value).length <= 200;
                }
                return true;
            })
    });

    onSubmit = async (values, formikBag) => {
        const { ceremony } = this.props;

        if (validateFileSizeLimit(values.photo)) {
            this.props.showToast({
                body: i18n.photoUploadModal.fileSizeLimitWarning,
                title: "Error",
                themeClass: "is-danger"
            });

            formikBag.setSubmitting(false);
            return;
        }

        if (await validateImageResolution(values.photo)) {
            this.props.showToast({
                body: i18n.photoUploadModal.imageResolutionWarning,
                title: "Error",
                themeClass: "is-danger"
            });

            formikBag.setSubmitting(false);
            return;
        }

        if (
            (this.props.block.type === "audio" ||
                this.props.block.type === "slideshow") &&
            values.photo instanceof FileList
        ) {
            if (values.photo.length > 100) {
                this.props.showToast({
                    body: i18n.photoUploadModal.fileLimitWarning,
                    title: "Error",
                    themeClass: "is-danger"
                });

                formikBag.setSubmitting(false);
                return;
            }
        }

        if (
            this.props.block.type === "speaker" &&
            values.photo instanceof FileList
        ) {
            if (
                values.photo.length + this.props.block.imageIds.length >
                ceremony.item.numberPhotos
            ) {
                this.props.showToast({
                    body: i18n.photoUploadModal.speakerFileLimitWarning,
                    title: "Error",
                    themeClass: "is-danger"
                });

                formikBag.setSubmitting(false);
                return;
            }
        }

        formikBag.setStatus({ progress: 0 });

        try {
            if (values.photo instanceof FileList) {
                const body = {};
                for (let i = 0; i < values.photo.length; i++) {
                    body[`files[${i}]`] = values.photo[i];
                }
                await this.api.postPhotoInBlock(
                    ceremony.item.id,
                    this.props.block.id,
                    body,
                    e => handleUploadProgress(e, formikBag.setStatus)
                );
            } else {
                await this.api.postPhotoInBlock(
                    ceremony.item.id,
                    this.props.block.id,
                    { "files[0]": values.photo },
                    e => handleUploadProgress(e, formikBag.setStatus)
                );
            }

            const {
                data: { data: photos }
            } = await this.api.getCeremonyPhotos(ceremony.item.id);
            const { entities } = normalize(photos, [photoSchema]);
            this.props.updateStoreEntities(entities);

            const {
                data: { data: blocks }
            } = await this.api.getCeremonyBlocks(ceremony.item.id);
            const normalizedBlocks = normalize(blocks, [blockSchema]);
            this.props.updateStoreEntities(normalizedBlocks.entities);

            formikBag.setStatus({ uploadProgress: 100 });

            // Wait for progress bar transition
            setTimeout(() => {
                formikBag.setSubmitting(false);
                this.props.hideModal();
            }, 350);
        } catch (e) {
            if (e instanceof ValidationError) {
                formikBag.setErrors(e.errors);
                formikBag.setSubmitting(false);
            } else {
                Sentry.captureException(e);
                console.error(e);
                formikBag.setSubmitting(false);
                this.props.showToast({
                    body: extractApiErrorMessage(e),
                    title: "Error",
                    themeClass: "is-danger"
                });
            }
        }
    };

    render() {
        return (
            <Formik
                initialValues={this.initialValues}
                onSubmit={this.onSubmit}
                validationSchema={this.baseValidationSchema}
                render={props => (
                    <BlockPhotoUploadModalView
                        deleteCrematory={() => this.deleteCrematory()}
                        hideModal={() => this.props.hideModal()}
                        isVisible={this.props.isVisible}
                        {...props}
                    />
                )}
            />
        );
    }
}

export default withRouter(BlockPhotoUploadModalContainer);
